/**
 * BaaS instance capabilities via MobileService `/v1/ib/console/*` → `v1/instances/{id}/features`
 * (+ caller permissions from `/v1/ib/baas/instances/{id}/my-permissions`).
 *
 * Product visibility rule: only show what permissions / instance features grant.
 * Rails (pix / stables / cards / issuer / efx) stay off until explicitly enabled.
 * Core console (entities / api / credit) defaults on when the feature row is absent.
 */

import { baasProxy, mobileConsoleRequest, resolveBaasProxyContext } from "./baasConsoleClient";
import { isBaasFullConsoleRole } from "../Pages/Private/BaasConsole/baasCapabilities";

/** Fallback only when my-permissions is unavailable (keeps admin/dev unblocked). */
const FULL_DEV_PERMISSIONS = [
  "console.read",
  "entities.create",
  "entities.read",
  "entities.update",
  "accounts.read",
  "accounts.create",
  "issuer.programs.create",
  "issuer.read",
  "api.keys.manage",
  "transactions.read",
  "transactions.write",
  "stables.operate",
  "credit.read",
  "users.manage",
  "reports.read",
  "console.write",
  "stables.read",
  "instances.create",
];

/**
 * Portal codes from my-permissions → FE capability tokens (mirrors IbPermissionCatalog).
 * evaluateCapability checks capability tokens (accounts.read), not portal codes (console.read).
 */
const CONSOLE_TO_CAPABILITY = {
  "console.read": [
    "console.read",
    "entities.read",
    "accounts.read",
    "issuer.read",
    "credit.read",
    "transactions.read",
    "stables.read",
  ],
  "console.write": [
    "console.write",
    "entities.create",
    "issuer.programs.create",
    "accounts.create",
    "instances.create",
  ],
  "api.manage": ["api.keys.manage"],
  "users.manage": ["users.manage"],
  "transactions.read": ["transactions.read", "stables.read"],
  "transactions.write": ["transactions.write", "stables.operate"],
  "reports.read": ["reports.read"],
};

const expandPortalPermissions = (portalPermissions) => {
  const set = new Set();
  for (const raw of portalPermissions || []) {
    const permission = String(raw || "").trim();
    if (!permission) continue;
    const mapped = CONSOLE_TO_CAPABILITY[permission];
    if (mapped) {
      mapped.forEach((item) => set.add(item));
    } else {
      set.add(permission);
    }
  }
  return [...set];
};

/** Safe defaults before features resolve — core on, rails off (no Stables flash). */
const SAFE_DEFAULT_PRODUCTS = {
  issuer: { enabled: false },
  entities: { enabled: true },
  api: { enabled: true },
  credit: { enabled: true },
  pix: { enabled: false },
  efx: { enabled: false },
  fiat: { enabled: false },
  stables: { enabled: false },
  cards: { enabled: false },
};

/** Legacy empty feature table → inherit all on (mirrors BE inheritAllWhenEmpty). */
const LEGACY_ALL_ON_PRODUCTS = {
  issuer: { enabled: true },
  entities: { enabled: true },
  api: { enabled: true },
  credit: { enabled: true },
  pix: { enabled: true },
  efx: { enabled: true },
  fiat: { enabled: true },
  stables: { enabled: true },
  cards: { enabled: true },
};

const FEATURE_TO_PRODUCT = {
  pix: "pix",
  "processing.pix": "pix",
  stables: "stables",
  "processing.stables": "stables",
  efx: "efx",
  crossborder: "efx",
  fiat: "fiat",
  entities: "entities",
  accounts: "entities",
  issuer: "issuer",
  cards: "cards",
  credit: "credit",
  api: "api",
};

const isFeatureEnabledFlag = (rawEnabled) =>
  rawEnabled !== false &&
  rawEnabled !== 0 &&
  rawEnabled !== "0" &&
  String(rawEnabled ?? "").toLowerCase() !== "false";

const mergeProducts = (base, overrides = {}) => ({
  issuer: { ...base.issuer, ...overrides.issuer },
  entities: { ...base.entities, ...overrides.entities },
  api: { ...base.api, ...overrides.api },
  credit: { ...base.credit, ...overrides.credit },
  pix: { ...base.pix, ...overrides.pix },
  efx: { ...base.efx, ...overrides.efx },
  fiat: { ...base.fiat, ...overrides.fiat },
  stables: { ...base.stables, ...overrides.stables },
  cards: { ...base.cards, ...overrides.cards },
});

/** Cards-only: Entidades + Emissor + crédito stay on even if banking rails are off. */
const applyCardsCoreProducts = (products) => {
  if (products?.cards?.enabled !== true) return products;
  return mergeProducts(products, {
    issuer: { enabled: true },
    entities: { enabled: true },
    credit: { enabled: true },
  });
};

/**
 * Map instance feature rows → console products.
 * Empty list → legacy all-on. Non-empty → rails default off; core default on;
 * only listed codes override (mirrors BE InstanceAllows / InstanceCoreProductOn).
 */
const mapFeaturesToProducts = (featuresPayload) => {
  const features = Array.isArray(featuresPayload?.features)
    ? featuresPayload.features
    : Array.isArray(featuresPayload)
      ? featuresPayload
      : [];

  if (!features.length) {
    return mergeProducts(LEGACY_ALL_ON_PRODUCTS);
  }

  const overrides = {};
  features.forEach((feature) => {
    const name = String(
      feature?.feature_code ||
        feature?.featureCode ||
        feature?.name ||
        feature?.feature ||
        feature?.id ||
        "",
    )
      .trim()
      .toLowerCase();
    const enabled = isFeatureEnabledFlag(
      feature?.enabled ?? feature?.active ?? feature?.Enabled,
    );
    const product = FEATURE_TO_PRODUCT[name];
    if (product) {
      overrides[product] = { enabled };
    }

    // cards gate also drives issuer panel (Cartões / programas / Emissor)
    // and Entidades / crédito (cards-only onboarding + program reserve).
    if (name === "cards" && enabled) {
      overrides.issuer = { enabled: true };
      overrides.entities = { enabled: true };
      overrides.credit = { enabled: true };
    }
  });

  return applyCardsCoreProducts(mergeProducts(SAFE_DEFAULT_PRODUCTS, overrides));
};

const normalizeProductsMap = (productsRaw) => {
  if (!productsRaw || typeof productsRaw !== "object" || Array.isArray(productsRaw)) {
    return null;
  }
  return Object.fromEntries(
    Object.entries(productsRaw).map(([key, value]) => [
      String(key).toLowerCase(),
      {
        enabled: isFeatureEnabledFlag(
          value?.enabled ?? value?.Enabled ?? value,
        ),
      },
    ]),
  );
};

const cache = new Map();

const fetchCallerCapabilityGrant = async (ctx) => {
  try {
    const data = await mobileConsoleRequest({
      method: "GET",
      path: `v1/ib/baas/instances/${ctx.instanceId}/my-permissions`,
    });
    const role = String(data?.role ?? data?.Role ?? "")
      .trim()
      .toLowerCase();
    const caps = data?.capability_permissions ?? data?.capabilityPermissions;
    const portal = Array.isArray(data?.permissions) ? data.permissions.map(String) : [];

    let permissions = null;
    // Instance admin / master: full capability surface — ignore partial portal / checkbox JSON.
    if (isBaasFullConsoleRole(role)) {
      permissions = [...FULL_DEV_PERMISSIONS];
    } else if (Array.isArray(caps) && caps.length > 0) {
      permissions = caps.map(String);
    } else if (portal.length > 0) {
      // Portal codes alone fail evaluateCapability (needs accounts.read etc.).
      permissions = expandPortalPermissions(portal);
    } else if (role) {
      permissions = [];
    }

    // Keep BE product map as-is (already instance/entity gated). Do not force all-on for admin.
    const products = normalizeProductsMap(data?.products ?? data?.Products ?? null);

    if (permissions != null) {
      return {
        permissions,
        role: role || null,
        products,
        entityId: data?.entity_id ?? data?.entityId ?? null,
        canViewProduction:
          data?.can_view_production !== false && data?.canViewProduction !== false,
        canViewHomolog:
          data?.can_view_homolog !== false && data?.canViewHomolog !== false,
      };
    }
  } catch {
    // Fail closed — never grant full console when my-permissions is unreachable.
    // (Previously FULL_DEV_PERMISSIONS left Operador able to manage users / settings.)
  }
  return {
    permissions: [],
    role: null,
    products: null,
    entityId: null,
    canViewProduction: true,
    canViewHomolog: true,
  };
};

/**
 * @param {object | string | null | undefined} instanceOrId
 * @returns {{ products: Record<string, { enabled: boolean }>, permissions: string[], role: string|null }}
 */
export const getBaasInstanceCapabilitiesSync = (instanceOrId) => {
  if (!instanceOrId) {
    return {
      products: mergeProducts(SAFE_DEFAULT_PRODUCTS),
      permissions: [],
      role: null,
      canViewProduction: true,
      canViewHomolog: true,
    };
  }

  const key =
    typeof instanceOrId === "object" ? instanceOrId.id : String(instanceOrId);
  if (cache.has(key)) {
    return cache.get(key);
  }

  return {
    products: mergeProducts(SAFE_DEFAULT_PRODUCTS),
    permissions: [],
    role: null,
    canViewProduction: true,
    canViewHomolog: true,
  };
};

/**
 * @param {object | string | null | undefined} instanceOrId
 */
export const getBaasInstanceCapabilities = async (instanceOrId) => {
  if (!instanceOrId) {
    return {
      products: mergeProducts(SAFE_DEFAULT_PRODUCTS),
      permissions: [],
      role: null,
      canViewProduction: true,
      canViewHomolog: true,
    };
  }

  const key =
    typeof instanceOrId === "object" ? instanceOrId.id : String(instanceOrId);

  try {
    const ctx =
      typeof instanceOrId === "object"
        ? resolveBaasProxyContext(instanceOrId)
        : resolveBaasProxyContext(instanceOrId);

    const [features, grant] = await Promise.all([
      baasProxy({
        instanceId: ctx.instanceId,
        environment: ctx.environment,
        method: "GET",
        baasPath: `v1/instances/${ctx.instanceId}/features`,
      }).catch(() => null),
      fetchCallerCapabilityGrant(ctx),
    ]);

    // Instance features as base; my-permissions products (entity-aware) override overlapping keys.
    // Admin roles no longer unlock every rail — Stables/Cartões stay hidden when feature is off.
    const fromInstance = mapFeaturesToProducts(features);
    const products = applyCardsCoreProducts(
      grant.products ? mergeProducts(fromInstance, grant.products) : fromInstance,
    );

    const capabilities = {
      products,
      permissions: grant.permissions,
      role: grant.role,
      canViewProduction: grant.canViewProduction !== false,
      canViewHomolog: grant.canViewHomolog !== false,
    };
    cache.set(key, capabilities);
    return capabilities;
  } catch {
    // Fail closed on network / proxy errors — deny writes until caps resolve.
    const fallback = {
      products: mergeProducts(SAFE_DEFAULT_PRODUCTS),
      permissions: [],
      role: null,
      canViewProduction: true,
      canViewHomolog: true,
    };
    cache.set(key, fallback);
    return fallback;
  }
};
